/* Landing screen */

import { useEffect, useRef, useState } from 'react';
import { Button, Logo } from './components.jsx';
import { Icon } from './icons.jsx';
import { HERO_PRESETS, topPercentLabel } from './presets.js';
import { Distribution, RangeNumber } from './result.jsx';

function LandingHeader({ onOpenLogin }) {
  return (
    <header className="app-header">
      <Logo size={36} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* История доступна только на экране резюме (splitview), на лендинге
            её намеренно нет. */}
        {/* TODO(post-hackathon): restore login UI. Backend ready (auth.py),
            anon->email merge implemented. Restore: uncomment this button +
            wire onOpenLogin in app.jsx to a login modal. */}
        {onOpenLogin ? (
          <Button variant="secondary" size="sm" onClick={onOpenLogin}>Войти</Button>
        ) : null}
      </div>
    </header>
  );
}

const CAROUSEL_INTERVAL_MS = 12000;

/* Hero carousel — classic stacked card with top progress bar, count-up
 * digits (via RangeNumber's useCountUp), staggered chip fade-in, and
 * click-to-prefill.
 *
 * Behavior contract:
 *  - Auto-advance every 8s; pause on hover/focus, resume on leave/blur
 *    WITHOUT resetting the elapsed time (so a 7s read isn't punished by
 *    another full 8s wait).
 *  - prefers-reduced-motion: don't rotate at all, freeze on first preset,
 *    drop the progress bar. (The CSS reduced-motion rule also zeroes
 *    transition/animation durations, so the bar would freeze even without
 *    this, but we hide it anyway to avoid showing a non-moving track.)
 *  - Clicking the card (or pressing Enter/Space on it) prefills the form
 *    with that preset's `form` and switches to the splitview screen.
 *  - Live region announces preset changes for screen readers (debounced
 *    1.5s after the change, so a fast cycle doesn't spam SR).
 *
 * `skillLabels`: optional slug -> human name map. When the landing knows
 * the catalog (post-boot) we render proper names; before that we fall back
 * to capitalised slugs so we never block the landing render.
 */
function HeroCarousel({ onPickPreset, skillLabels }) {
  const presets = HERO_PRESETS;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const startedAtRef = useRef(performance.now());
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const reducedRef = useRef(false);

  // Detect reduced-motion once. We never re-evaluate; if a user toggles
  // the OS setting mid-session they can refresh the page.
  useEffect(() => {
    reducedRef.current =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Schedule the next advance. If paused (hover/focus) we don't schedule —
  // resume re-runs this effect with the remaining time. Reduced-motion
  // short-circuits to no timer at all.
  useEffect(() => {
    if (reducedRef.current) return undefined;
    if (paused) return undefined;
    const remaining = Math.max(0, CAROUSEL_INTERVAL_MS - elapsedRef.current);
    startedAtRef.current = performance.now();
    timerRef.current = setTimeout(() => {
      elapsedRef.current = 0;
      setIndex((i) => (i + 1) % presets.length);
    }, remaining);
    return () => clearTimeout(timerRef.current);
  }, [paused, index, presets.length]);

  // Live-region announcement (debounced) so screen readers don't spam.
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const p = presets[index];
    const t = setTimeout(() => {
      setAnnouncement(
        `Пример: ${p.roleLabel}, вилка ${p.min.toLocaleString('ru-RU')}–${p.max.toLocaleString('ru-RU')} рублей`,
      );
    }, 1500);
    return () => clearTimeout(t);
  }, [index, presets]);

  const pause = () => {
    if (paused) return;
    elapsedRef.current += performance.now() - startedAtRef.current;
    setPaused(true);
  };
  const resume = () => {
    if (!paused) return;
    setPaused(false); // effect above will reschedule with the remaining time
  };

  const handlePick = () => {
    setFlashOn(true);
    setTimeout(() => setFlashOn(false), 220);
    onPickPreset(presets[index]);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePick();
    }
  };

  const preset = presets[index];
  const mid = Math.round((preset.min + preset.max) / 2);
  const topPct = topPercentLabel(preset.percentile);
  const labelOf = (slug) =>
    (skillLabels && skillLabels[slug]) || slug.replace(/-/g, ' ');

  // Key the inner content on `index` so React fully unmounts and re-mounts
  // chip nodes — that re-fires the CSS chip-stagger animation cleanly
  // instead of trying to interrupt an in-progress one.
  return (
    <div
      className={
        'hero-preview hero-carousel reveal' +
        (hovered ? ' is-hovered' : '') +
        (flashOn ? ' is-flash' : '') +
        (reducedRef.current ? ' is-reduced' : '')
      }
      style={{ animationDelay: '120ms' }}
      role="button"
      tabIndex={0}
      aria-label={`Примерить пример: ${preset.roleLabel}`}
      onMouseEnter={() => { setHovered(true); pause(); }}
      onMouseLeave={() => { setHovered(false); resume(); }}
      onFocus={pause}
      onBlur={resume}
      onClick={handlePick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="hero-progress"
        aria-hidden="true"
        // Keyed on index ONLY (not on paused state) so the CSS animation
        // restarts cleanly on tick but freezes in place on hover and
        // resumes from the same spot on mouseleave — matching the JS
        // elapsed-time bookkeeping.
        key={`progress-${index}`}
      />

      <div className="hero-hint" aria-hidden="true">
        <Icon name="arrow-right" size={12} stroke={2.4} /> примерить
      </div>

      <div className="hero-header" key={`header-${index}`}>
        <h3 className="hero-role-title">{preset.roleLabel}</h3>
        {/* <span className="t-caption t-muted">Вилка дохода в месяц</span> */}
      </div>

      <RangeNumber min={preset.min} max={preset.max} />

      <div style={{ marginTop: 20 }}>
        <Distribution min={preset.min} max={preset.max} mid={mid} />
        <div style={{ fontSize: 13, color: 'var(--t-text-muted)', marginTop: 8 }}>
          Вы в топ {topPct}% IT-рынка
        </div>
      </div>

      <div className="hero-chips" key={`chips-${index}`}>
        {preset.form.skills.map((slug, i) => (
          <span
            key={slug}
            className="chip hero-chip"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            {labelOf(slug)}
          </span>
        ))}
      </div>

      <div className="hero-recs" key={`recs-${index}`}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>Как повысить вилку</div>
        <div className="stack-2">
          {(preset.recs || []).map((r) => (
            <div className="rec" key={r.slug}>
              <div className="rec-icon"><Icon name="code" size={18} /></div>
              <div className="rec-body">
                <div className="rec-title">{r.label}</div>
                <div className="rec-sub">навык</div>
              </div>
              <Icon name="chevron-right" size={18} style={{ color: 'var(--t-text-muted)' }} />
            </div>
          ))}
        </div>
      </div>

      <div className="sr-only" aria-live="polite">{announcement}</div>
    </div>
  );
}

function Stat({ n, t }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>{n}</div>
      <div style={{ fontSize: 13, color: 'var(--t-text-muted)' }}>{t}</div>
    </div>
  );
}

export function Landing({ onStart, onOpenLogin, skillLabels }) {
  return (
    <div className="screen-enter">
      <LandingHeader onOpenLogin={onOpenLogin} />

      <section style={{ padding: '64px 0 96px' }}>
        <div className="container">
          <div
            className="hero-grid"
            style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 64, alignItems: 'center' }}
          >
            <div className="stack-6">
              <div className="reveal">
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', background: 'var(--t-bg)',
                  borderRadius: 9999, fontSize: 13, fontWeight: 500
                }}>
                  <span style={{ width: 6, height: 6, background: 'var(--t-yellow)', borderRadius: '50%' }}></span>
                  Хакатон Т-Банк / BMSTU 2026 — оценка резюме
                </span>
              </div>
              <h1 className="t-display reveal" style={{ margin: 0, animationDelay: '60ms', textWrap: 'pretty' }}>
                Узнайте, сколько вы стоите на рынке
              </h1>
              <p className="t-body-l t-muted reveal" style={{ margin: 0, maxWidth: 520, animationDelay: '120ms' }}>
                Заполните резюме — получите вилку дохода и рекомендации, как её повысить. Правьте поля и смотрите, как меняется результат.
              </p>
              <div className="reveal" style={{ display: 'flex', gap: 12, animationDelay: '180ms' }}>
                <Button variant="primary" iconRight="arrow-right" onClick={() => onStart(false)}>
                  Оценить резюме
                </Button>
              </div>
              <div className="reveal" style={{ display: 'flex', gap: 24, marginTop: 8, animationDelay: '240ms' }}>
                <Stat n="2 мин" t="на первый расчёт" />
                <Stat n="16 000" t="вакансий в выборке" />
                <Stat n="880" t="уникальных навыков" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <HeroCarousel
                skillLabels={skillLabels}
                onPickPreset={(preset) => onStart(preset.form)}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-bg" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="reveal stack-3" style={{ marginBottom: 40, maxWidth: 640 }}>
            <span className="t-caption t-muted">Как это работает</span>
            <h2 className="t-h1" style={{ margin: 0 }}>Три шага до новой вилки</h2>
          </div>
          <div
            className="steps-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}
          >
            {[
              { n: 1, icon: 'file-text', title: 'Заполните резюме', text: 'Роль, опыт, навыки, регион. Чем подробнее — тем точнее оценка.' },
              { n: 2, icon: 'sparkles',  title: 'Получите вилку',   text: 'Сравниваем с похожими вакансиями. Показываем минимум, максимум и распределение рынка.' },
              { n: 3, icon: 'arrow-up-right', title: 'Правьте и растите', text: 'Каждая рекомендация даёт прирост в рублях. Сохраняйте версии и сравнивайте.' },
            ].map((s, i) => (
              <div key={s.n} className="step-card reveal" style={{ animationDelay: 120 + i * 80 + 'ms' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="step-num">{s.n}</div>
                  <Icon name={s.icon} size={20} style={{ color: 'var(--t-text-muted)' }} />
                </div>
                <h3 className="t-h3" style={{ margin: '8px 0 0' }}>{s.title}</h3>
                <p className="t-body t-muted" style={{ margin: 0 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '64px 0' }}>
        <div className="container">
          <div
            className="card"
            style={{
              background: 'var(--t-black)', color: '#fff',
              padding: '40px 48px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
              flexWrap: 'wrap'
            }}
          >
            <div style={{ maxWidth: 560 }}>
              <h2 className="t-h2" style={{ margin: 0 }}>Готовы посмотреть на свою рыночную вилку?</h2>
              <p className="t-body-l" style={{ margin: '8px 0 0', color: '#A0A6AE' }}>
                Без регистрации. Данные не сохраняются на сервере.
              </p>
            </div>
            <Button variant="primary" iconRight="arrow-right" onClick={() => onStart(false)}>
              Начать
            </Button>
          </div>
        </div>
      </section>

      <footer style={{
        borderTop: '1px solid var(--t-border)', padding: '24px 0',
        color: 'var(--t-text-muted)', fontSize: 13
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>Сделано на хакатоне от Т-Банка, 2026</div>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>О сервисе</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Контакты</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
